import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Search, MapPin, ArrowLeft, Plus, ExternalLink, Star, 
  MessageSquare, AlertCircle, CheckCircle, Loader2, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { API } from "../../App";

const INDUSTRIES = [
  { value: "hospitality", label: "Hospitality" },
  { value: "trades", label: "Trades" },
  { value: "health_wellness", label: "Health & Wellness" },
  { value: "professional_services", label: "Professional Services" },
  { value: "retail", label: "Retail" },
  { value: "other", label: "Other" }
];

const CATEGORY_SUGGESTIONS = {
  hospitality: ["hotels", "restaurants", "cafes", "pubs", "bars", "B&Bs", "guest houses"],
  trades: ["plumbers", "electricians", "builders", "roofers", "carpenters", "painters", "landscapers"],
  health_wellness: ["gyms", "yoga studios", "beauty salons", "spas", "dentists", "physiotherapists"],
  professional_services: ["accountants", "solicitors", "architects", "estate agents", "consultants"],
  retail: ["boutiques", "florists", "gift shops", "jewellers", "furniture shops"],
  other: ["garages", "vets", "pet shops", "tutors", "photographers"]
};

const ProspectFinder = () => {
  const [searchParams, setSearchParams] = useState({
    location: "",
    radius_miles: 5,
    category: ""
  });
  const [selectedIndustry, setSelectedIndustry] = useState("hospitality");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedPlaces, setSelectedPlaces] = useState(new Set());
  const [adding, setAdding] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchParams.location || !searchParams.category) {
      toast.error("Please enter location and business category");
      return;
    }

    setLoading(true);
    setResults(null);
    setSelectedPlaces(new Set());

    try {
      const response = await axios.post(`${API}/lead-engine/finder/search`, {
        location: searchParams.location,
        radius_miles: searchParams.radius_miles,
        category: searchParams.category,
        max_results: 20
      });
      setResults(response.data);
      
      if (response.data.total_found === 0) {
        toast.info("No businesses with websites found in this area");
      } else {
        toast.success(`Found ${response.data.total_found} businesses with websites`);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || "Rate limit exceeded. Please wait.");
      } else {
        toast.error(error.response?.data?.detail || "Search failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (placeId) => {
    const newSelected = new Set(selectedPlaces);
    if (newSelected.has(placeId)) {
      newSelected.delete(placeId);
    } else {
      newSelected.add(placeId);
    }
    setSelectedPlaces(newSelected);
  };

  const selectAllValid = () => {
    if (!results) return;
    const validPlaceIds = results.results
      .filter(r => !r.is_excluded)
      .map(r => r.place_id);
    setSelectedPlaces(new Set(validPlaceIds));
  };

  const clearSelection = () => {
    setSelectedPlaces(new Set());
  };

  const handleAddProspects = async () => {
    if (selectedPlaces.size === 0) {
      toast.error("Please select at least one business");
      return;
    }

    const placesToAdd = results.results.filter(r => selectedPlaces.has(r.place_id));
    
    setAdding(true);
    try {
      const response = await axios.post(`${API}/lead-engine/finder/add-prospects`, {
        places: placesToAdd,
        industry: selectedIndustry
      });
      
      toast.success(response.data.message);
      setSelectedPlaces(new Set());
      
      // Remove added places from results
      if (response.data.created > 0) {
        const addedIds = new Set(placesToAdd.map(p => p.place_id));
        setResults({
          ...results,
          results: results.results.filter(r => !addedIds.has(r.place_id)),
          total_found: results.total_found - response.data.created
        });
      }
    } catch (error) {
      toast.error("Failed to add prospects");
    } finally {
      setAdding(false);
    }
  };

  return (
    <AdminLayout title="Prospect Finder">
      <div className="space-y-6">
        {/* Back */}
        <Link to="/admin/lead-engine" className="inline-flex items-center gap-2 text-sm text-[#8A8A8A] hover:text-[#F5F4EF]">
          <ArrowLeft className="w-4 h-4" /> Back to Lead Engine
        </Link>

        {/* Header */}
        <div>
          <h2 className="text-xl font-bold">Prospect Finder</h2>
          <p className="text-sm text-[#8A8A8A]">Search Google Places for businesses that might need a new website</p>
        </div>

        {/* Search Form */}
        <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Location <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                  <input
                    type="text"
                    value={searchParams.location}
                    onChange={(e) => setSearchParams({ ...searchParams, location: e.target.value })}
                    className="input-field pl-10 w-full"
                    placeholder="Brighton, UK"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Business Category <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                  <input
                    type="text"
                    value={searchParams.category}
                    onChange={(e) => setSearchParams({ ...searchParams, category: e.target.value })}
                    className="input-field pl-10 w-full"
                    placeholder="hotels, plumbers, salons..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Radius</label>
                <select
                  value={searchParams.radius_miles}
                  onChange={(e) => setSearchParams({ ...searchParams, radius_miles: parseInt(e.target.value) })}
                  className="input-field w-full"
                >
                  <option value={1}>1 mile</option>
                  <option value={3}>3 miles</option>
                  <option value={5}>5 miles</option>
                  <option value={10}>10 miles</option>
                  <option value={15}>15 miles</option>
                  <option value={25}>25 miles</option>
                </select>
              </div>
            </div>

            {/* Category Suggestions */}
            <div>
              <p className="text-xs text-[#8A8A8A] mb-2">Suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_SUGGESTIONS[selectedIndustry]?.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSearchParams({ ...searchParams, category: cat })}
                    className={`px-3 py-1 text-xs border transition-colors ${
                      searchParams.category === cat
                        ? 'bg-[#5F8FB8] border-[#5F8FB8] text-white'
                        : 'border-[#2E2E2E] text-[#8A8A8A] hover:border-[#5F8FB8]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-4">
                <label className="text-sm">Save as industry:</label>
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="input-field"
                >
                  {INDUSTRIES.map((ind) => (
                    <option key={ind.value} value={ind.value}>{ind.label}</option>
                  ))}
                </select>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="btn-primary inline-flex items-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Searching...</>
                ) : (
                  <><Search className="w-4 h-4" /> Search</>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Results */}
        {results && (
          <div className="bg-[#1C1C1C] border border-[#2E2E2E]">
            {/* Results Header */}
            <div className="p-4 border-b border-[#2E2E2E] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="font-medium">
                  {results.total_found} businesses found
                </h3>
                <p className="text-xs text-[#8A8A8A]">
                  {results.category} near {results.location} ({results.radius_miles} mile radius)
                </p>
              </div>
              
              {results.results.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#8A8A8A]">
                    {selectedPlaces.size} selected
                  </span>
                  <button
                    onClick={selectAllValid}
                    className="btn-secondary text-xs"
                  >
                    Select All Valid
                  </button>
                  {selectedPlaces.size > 0 && (
                    <>
                      <button
                        onClick={clearSelection}
                        className="btn-secondary text-xs"
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleAddProspects}
                        disabled={adding}
                        className="btn-primary text-xs inline-flex items-center gap-1"
                      >
                        {adding ? (
                          <><Loader2 className="w-3 h-3 animate-spin" /> Adding...</>
                        ) : (
                          <><Plus className="w-3 h-3" /> Add {selectedPlaces.size} to Prospects</>
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Results Table */}
            {results.results.length === 0 ? (
              <div className="p-8 text-center text-[#8A8A8A]">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No businesses with websites found</p>
                <p className="text-xs mt-2">Try a different category or expand the radius</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2E2E2E] text-left text-xs text-[#8A8A8A] uppercase">
                      <th className="p-4 w-10"></th>
                      <th className="p-4">Business</th>
                      <th className="p-4">Website</th>
                      <th className="p-4">Rating</th>
                      <th className="p-4">Reviews</th>
                      <th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((place) => (
                      <tr 
                        key={place.place_id} 
                        className={`border-b border-[#2E2E2E] ${
                          place.is_excluded ? 'opacity-50 bg-[#1A1A1A]' : 'hover:bg-[#252525]'
                        }`}
                      >
                        <td className="p-4">
                          {!place.is_excluded && (
                            <input
                              type="checkbox"
                              checked={selectedPlaces.has(place.place_id)}
                              onChange={() => toggleSelection(place.place_id)}
                              className="w-4 h-4 rounded border-[#2E2E2E] bg-[#0E0E0E] text-[#5F8FB8] focus:ring-[#5F8FB8]"
                            />
                          )}
                        </td>
                        <td className="p-4">
                          <div className="font-medium">{place.name}</div>
                          <div className="text-xs text-[#8A8A8A] mt-1">{place.address}</div>
                        </td>
                        <td className="p-4">
                          <a
                            href={place.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#5F8FB8] hover:underline text-sm inline-flex items-center gap-1"
                          >
                            {new URL(place.website).hostname.replace('www.', '')}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="p-4">
                          {place.rating ? (
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <span className="text-sm">{place.rating.toFixed(1)}</span>
                            </div>
                          ) : (
                            <span className="text-[#8A8A8A]">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          {place.review_count ? (
                            <div className="flex items-center gap-1 text-sm text-[#8A8A8A]">
                              <MessageSquare className="w-3 h-3" />
                              {place.review_count}
                            </div>
                          ) : (
                            <span className="text-[#8A8A8A]">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          {place.is_excluded ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 text-xs">
                              <X className="w-3 h-3" /> Excluded
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 text-xs">
                              <CheckCircle className="w-3 h-3" /> Valid
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        {!results && !loading && (
          <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
            <h3 className="font-medium mb-3">How Prospect Finder Works</h3>
            <ul className="space-y-2 text-sm text-[#8A8A8A]">
              <li>1. Enter a UK location and business category</li>
              <li>2. We search Google Places for businesses with websites</li>
              <li>3. Agencies, franchises, and major chains are auto-flagged</li>
              <li>4. Select businesses to add as prospects</li>
              <li>5. Scan their websites later to calculate priority scores</li>
            </ul>
            <div className="mt-4 p-3 bg-[#0E0E0E] border border-[#2E2E2E]">
              <p className="text-xs text-[#8A8A8A]">
                <strong className="text-[#F5F4EF]">Note:</strong> Google Places API has rate limits. 
                If you see a rate limit error, wait 60 seconds before searching again.
              </p>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProspectFinder;
